import { useState, useEffect, useCallback, useRef } from "react";
import ProjetoCentral from "./ProjetoCentral";
import api from "../api";
import { useTheme } from "../components/Theme";

const ORANGE = "#FF6B2B";

const ETAPAS_PROJETO = [
  { id: "planejamento", label: "Planejamento",    color: "#6366f1" },
  { id: "em_andamento", label: "Em andamento",    color: "#f59e0b" },
  { id: "em_revisao",   label: "Em revisão",      color: "#3b82f6" },
  { id: "aguardando",   label: "Aguardando",      color: "#e879f9" },
  { id: "concluido",    label: "Concluído",       color: "#10b981" },
  { id: "cancelado",    label: "Cancelado",       color: "#ef4444" },
  { id: "pausado",      label: "Pausado",         color: "#8E8E93" },
];

const GRUPOS = [
  { id: "ativos",     label: "Em andamento",         color: "#f59e0b", stages: ["planejamento","em_andamento","em_revisao","aguardando"] },
  { id: "concluidos", label: "Concluídos",            color: "#10b981", stages: ["concluido"] },
  { id: "pausados",   label: "Pausados / Cancelados", color: "#8E8E93", stages: ["pausado","cancelado"] },
];

function fmt(v) { return v ? `R$${Number(v).toLocaleString("pt-BR")}` : ""; }
function fmtData(d) { if(!d) return ""; const dt = new Date(d); const local = new Date(dt.getTime() + dt.getTimezoneOffset()*60000); return local.toLocaleDateString("pt-BR"); }

function Avatar({ nome, size = 28 }) {
  const iniciais = nome?.split(" ").slice(0,2).map(n=>n[0]).join("").toUpperCase() || "?";
  const cores = ["#6366f1","#f59e0b","#10b981","#3b82f6","#e879f9","#ef4444",ORANGE];
  const cor = cores[nome?.charCodeAt(0) % cores.length] || ORANGE;
  return (
    <div title={nome} style={{ width:size, height:size, borderRadius:"50%", background:cor+"33", border:`2px solid ${cor}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*.38, fontWeight:700, color:cor, flexShrink:0, fontFamily:"'Inter',sans-serif" }}>
      {iniciais}
    </div>
  );
}

function CronogramaBar({ inicio, fim, concluido }) {
  const { theme } = useTheme();
  if (!inicio && !fim) return <div style={{ color:theme.textMuted, fontSize:12 }}>—</div>;
  const hoje = new Date();
  const dI = inicio ? new Date(inicio) : hoje;
  const dF = fim    ? new Date(fim)    : new Date(hoje.getTime()+30*24*60*60*1000);
  const total = (dF-dI)/(1000*60*60*24);
  const dec   = (hoje-dI)/(1000*60*60*24);
  const pct   = Math.max(0, Math.min(100, (dec/total)*100));
  const atrasado = hoje > dF && !concluido;
  const pctFinal = concluido ? 100 : pct;
  const cor = concluido ? "#10b981" : atrasado ? "#ef4444" : pct > 75 ? "#f59e0b" : "#10b981";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, width:"100%" }}>
      <div style={{ flex:1, height:6, background:theme.bgInput, borderRadius:99, overflow:"hidden" }}>
        <div style={{ width:`${pctFinal}%`, height:"100%", background:cor, borderRadius:99 }}/>
      </div>
      <span style={{ fontSize:10, color:concluido?"#10b981":atrasado?"#ef4444":theme.textMuted, fontWeight:(atrasado||concluido)?700:400, whiteSpace:"nowrap" }}>
        {concluido ? "✓ Concluído" : atrasado ? "⚠️ Atrasado" : `${Math.round(pctFinal)}%`}
      </span>
    </div>
  );
}

function EtapaBadge({ stage, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const etapa = ETAPAS_PROJETO.find(e=>e.id===stage) || ETAPAS_PROJETO[0];
  useEffect(() => {
    const h = (e) => { if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <div onClick={(e) => { e.stopPropagation(); setOpen(o=>!o); }} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 12px", borderRadius:20, fontSize:11.5, fontWeight:700, color:etapa.color, background:etapa.color+"1E", cursor:"pointer", whiteSpace:"nowrap", userSelect:"none" }}>
        {etapa.label}<span style={{ fontSize:9, opacity:.6, marginLeft:1 }}>▾</span>
      </div>
      {open && (
        <div style={{ position:"absolute", top:"110%", left:0, zIndex:500, background:"#2C2C2F", border:"1px solid #3D3D42", borderRadius:12, padding:6, minWidth:180, boxShadow:"0 12px 40px rgba(0,0,0,.5)" }}>
          {ETAPAS_PROJETO.map(e=>(
            <div key={e.id} onClick={()=>{ onChange(e.id); setOpen(false); }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", borderRadius:8, cursor:"pointer" }}
              onMouseEnter={ev=>ev.currentTarget.style.background=e.color+"18"}
              onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
              <div style={{ width:10, height:10, borderRadius:3, background:e.color }}/>
              <span style={{ fontSize:13, color:"#F2F2F7", fontWeight:stage===e.id?600:400 }}>{e.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InlineEdit({ value, onChange, placeholder="—", style:s={} }) {
  const { theme } = useTheme();
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value||"");
  const ref = useRef();
  useEffect(() => { setVal(value||""); }, [value]);
  useEffect(() => { if(editing && ref.current) ref.current.focus(); }, [editing]);
  const commit = () => { setEditing(false); if(String(val)!==String(value||"")) onChange(val); };
  if (editing) return (
    <input ref={ref} value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{ if(e.key==="Enter")commit(); if(e.key==="Escape"){setVal(value||"");setEditing(false);} }}
      style={{ background:"transparent", border:"none", borderBottom:`1.5px solid ${ORANGE}`, outline:"none", color:theme.text, fontSize:13, width:"100%", padding:"2px 0", fontFamily:"'Inter',sans-serif", ...s }}/>
  );
  return (
    <div onClick={()=>setEditing(true)} style={{ cursor:"text", color:value?theme.text:theme.textMuted, fontSize:13, padding:"2px 4px", borderRadius:6, minWidth:30, ...s }}
      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,107,43,.08)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {value||<span style={{ color:theme.textMuted, fontSize:12 }}>{placeholder}</span>}
    </div>
  );
}

function ModalProjeto({ projeto, onClose, onSave, onDelete, usuarios }) {
  const { theme } = useTheme();
  const isNew = !projeto?._id;
  const [form, setForm] = useState({
    nome: projeto?.nome || "",
    empresa: projeto?.empresa || "",
    telefone: projeto?.telefone || "",
    email: projeto?.email || "",
    servico: projeto?.servico || "",
    local: projeto?.local || "",
    responsavelNome: projeto?.responsavelNome || projeto?.nomeResponsavel || "",
    stageProjeto: projeto?.stageProjeto || "planejamento",
    isProjeto: true,
    valor: projeto?.valor || "",
    dataInicio: projeto?.dataInicio ? new Date(new Date(projeto.dataInicio).getTime() + new Date(projeto.dataInicio).getTimezoneOffset()*60000).toISOString().slice(0,10) : "",
    dataFechamento: projeto?.dataFechamento ? new Date(new Date(projeto.dataFechamento).getTime() + new Date(projeto.dataFechamento).getTimezoneOffset()*60000).toISOString().slice(0,10) : "",
    probabilidade: projeto?.probabilidade || "",
    obs: projeto?.obs || "",
    // Manter stage do kanban se existir, senão usa prospeccao
    stage: projeto?.stage || "prospeccao",
  });
  const [nota, setNota] = useState("");
  const [load, setLoad] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = { width:"100%", boxSizing:"border-box", background:theme.bgInput, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"9px 12px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, fontFamily:"'Inter',sans-serif", transition:"border-color .15s" };

  const salvar = async () => {
    if (!form.nome.trim()) return alert("Nome do projeto é obrigatório");
    setLoad(true);
    try { await onSave({ ...projeto, ...form, isProjeto: true, tipo: "projeto" }); }
    catch(e) { alert(e.response?.data?.erro || "Erro ao salvar"); }
    finally { setLoad(false); }
  };

  const addNota = async () => {
    if (!nota.trim() || !projeto?._id) return;
    await api.post(`/leads/${projeto._id}/atividades`, { tipo:"nota", descricao:nota });
    setNota(""); onClose();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:20, padding:32, width:"100%", maxWidth:560, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
          <div>
            <div style={{ fontSize:11, color:ORANGE, fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>PROJETO</div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:theme.text }}>{form.nome||"Novo Projeto"}</h2>
          </div>
          <button onClick={onClose} style={{ background:theme.bgInput, border:"none", borderRadius:8, width:32, height:32, color:theme.textMuted, fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
        </div>

        {/* Etapa de projeto */}
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:8, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Etapa do projeto</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {ETAPAS_PROJETO.map(e=>(
              <button key={e.id} onClick={()=>set("stageProjeto",e.id)} style={{ padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`2px solid ${form.stageProjeto===e.id?e.color:theme.border}`, background:form.stageProjeto===e.id?e.color+"22":"transparent", color:form.stageProjeto===e.id?e.color:theme.textMuted, transition:"all .15s" }}>
                {e.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
          {[["Nome do projeto *","nome"],["Empresa / Cliente","empresa"],["Telefone / Contato","telefone"],["Email","email"],["Serviço / Escopo","servico"],["Local do projeto","local"]].map(([label,key])=>(
            <div key={key}>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
              <input value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Valor (R$)</label>
            <input type="number" value={form.valor||""} onChange={e=>set("valor",e.target.value)} style={inp} placeholder="0"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Probabilidade %</label>
            <input type="number" min="0" max="100" value={form.probabilidade||""} onChange={e=>set("probabilidade",e.target.value)} style={inp} placeholder="0"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Responsável</label>
            <select value={form.responsavelNome||""} onChange={e=>set("responsavelNome",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              <option value="">Selecionar...</option>
              {usuarios.map(u=><option key={u._id} value={u.nome}>{u.nome}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
          {[["Data de início","dataInicio"],["Data de fechamento","dataFechamento"]].map(([label,key])=>(
            <div key={key}>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
              <input type="date" value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
          ))}
        </div>

        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Observações</label>
          <textarea value={form.obs||""} onChange={e=>set("obs",e.target.value)} rows={3} style={{ ...inp, resize:"vertical" }} placeholder="Escopo, entregáveis, condições..."
            onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
        </div>

        {!isNew && projeto?.atividades?.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:8, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Histórico</label>
            <div style={{ maxHeight:140, overflowY:"auto" }}>
              {[...projeto.atividades].reverse().map((a,i)=>(
                <div key={i} style={{ background:theme.bgInput, borderRadius:8, padding:"9px 12px", marginBottom:6 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                    <span style={{ color:ORANGE, fontWeight:600, fontSize:12 }}>{a.nomeUsuario}</span>
                    <span style={{ fontSize:11, color:theme.textMuted }}>{new Date(a.createdAt).toLocaleDateString("pt-BR")}</span>
                  </div>
                  <div style={{ fontSize:13, color:theme.textSub }}>{a.descricao}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isNew && (
          <div style={{ display:"flex", gap:8, marginBottom:20 }}>
            <input value={nota} onChange={e=>setNota(e.target.value)} placeholder="Adicionar nota..."
              style={{ ...inp, flex:1 }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            <button onClick={addNota} style={{ background:ORANGE, border:"none", borderRadius:10, padding:"9px 16px", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700 }}>+</button>
          </div>
        )}

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={salvar} disabled={load} style={{ flex:1, background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:12, padding:"13px 0", color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:load?.7:1, boxShadow:`0 4px 14px ${ORANGE}44` }}>
            {load?"Salvando...":"Salvar projeto"}
          </button>
          {!isNew && <button onClick={()=>onDelete(projeto._id)} style={{ background:"#ef444418", border:"1px solid #ef444433", borderRadius:12, padding:"13px 18px", color:"#ef4444", fontWeight:600, fontSize:14, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

function NovaLinhaRow({ stageDefault, onAdd, onCancel }) {
  const { theme } = useTheme();
  const [nome, setNome] = useState("");
  const ref = useRef();
  useEffect(()=>{ ref.current?.focus(); },[]);
  return (
    <tr style={{ background:theme.bgInput }}>
      <td style={{ padding:"7px 10px", width:36 }}><input type="checkbox" disabled/></td>
      <td style={{ padding:"7px 14px" }} colSpan={2}>
        <input ref={ref} value={nome} onChange={e=>setNome(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"&&nome.trim())onAdd(nome.trim(),stageDefault); if(e.key==="Escape")onCancel(); }}
          placeholder="Nome do projeto... (Enter para adicionar)"
          style={{ background:"transparent", border:"none", borderBottom:`1.5px solid ${ORANGE}`, outline:"none", color:theme.text, fontSize:13, width:"100%", padding:"2px 0", fontFamily:"'Inter',sans-serif" }}/>
      </td>
      <td colSpan={5} style={{ padding:"7px 12px" }}>
        <span style={{ fontSize:11, color:theme.textMuted }}>↵ Enter para adicionar · Esc para cancelar</span>
      </td>
    </tr>
  );
}

export default function Projetos({ currentUser }) {
  const { theme } = useTheme();
  const [projetos, setProjetos]   = useState([]);
  const [dragProj, setDragProj] = useState(null);
  const [usuarios, setUsuarios]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [central, setCentral]      = useState(null);
  const [search, setSearch]       = useState("");
  const [novaLinhaGrupo, setNovaLinhaGrupo] = useState(null);
  const [colapsados, setColapsados] = useState({});
  const [selected, setSelected]   = useState([]);

  const carregar = useCallback(async () => {
    try {
      const [leadsRes, usersRes] = await Promise.all([
        api.get("/leads?limit=1000&tipo=projeto"),
        api.get("/users"),
      ]);
      // Mostra só leads marcados como projeto
      setProjetos(leadsRes.data.leads);
      setUsuarios(usersRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (form) => {
    try {
      if (form._id) {
        const { data } = await api.put(`/leads/${form._id}`, form);
        setProjetos(prev => prev.map(p => p._id === data._id ? data : p));
      } else {
        const { data } = await api.post("/leads", {
          ...form,
          isProjeto: true,
          stage: "prospeccao", // stage do kanban não muda
          stageProjeto: form.stageProjeto || "planejamento",
          origem: "Outro",
        });
        setProjetos(prev => [data, ...prev]);
      }
      setModal(null);
    } catch(e) {
      alert(e.response?.data?.erro || "Erro ao salvar projeto");
    }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir este projeto?")) return;
    await api.delete(`/leads/${id}`);
    setProjetos(prev => prev.filter(p => p._id !== id));
    setModal(null);
  };

  const atualizarCampo = async (id, campo, valor) => {
    try {
      const { data } = await api.put(`/leads/${id}`, { [campo]: valor });
      setProjetos(prev => prev.map(p => p._id === id ? { ...p, ...data } : p));
    } catch {}
  };

  const reordenarProjetos = (lista, fromId, toId) => {
    const from = lista.findIndex(p=>p._id===fromId), to = lista.findIndex(p=>p._id===toId);
    if(from<0||to<0||from===to) return;
    const arr=[...lista]; const [m]=arr.splice(from,1); arr.splice(to,0,m);
    const ordemMap={}; arr.forEach((p,idx)=>{ ordemMap[p._id]=idx; });
    setProjetos(prev=>prev.map(p=> ordemMap[p._id]!==undefined ? {...p, ordemProjeto:ordemMap[p._id]} : p));
    arr.forEach((p,idx)=> api.put(`/leads/${p._id}`, { ordemProjeto: idx }).catch(()=>{}));
  };
  const atualizarStageProjeto = async (id, stageProjeto) => {
    try {
      await api.put(`/leads/${id}`, { stageProjeto });
      setProjetos(prev => prev.map(p => p._id === id ? { ...p, stageProjeto } : p));
    } catch {}
  };

  const adicionarProjeto = async (nome, stageDefault) => {
    try {
      const { data } = await api.post("/leads", {
        nome, tipo: "projeto", isProjeto: true, stage: "prospeccao",
        stageProjeto: stageDefault, valor: 0, origem: "Outro",
      });
      setProjetos(prev => [data, ...prev]);
      setNovaLinhaGrupo(null);
    } catch(e) { alert(e.response?.data?.erro || "Erro ao criar projeto"); }
  };

  const filtered = projetos.filter(p => {
    const q = search.toLowerCase();
    return !q || p.nome?.toLowerCase().includes(q) || p.empresa?.toLowerCase().includes(q);
  });

  const getGrupo = (grupo) => filtered.filter(p => grupo.stages.includes(p.stageProjeto || "planejamento"));

  const TH = ({ children, w, center }) => (
    <th style={{ padding:"10px 14px", fontSize:11, color:theme.textMuted, fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", textAlign:center?"center":"left", width:w, minWidth:w, borderBottom:`1px solid ${theme.border}`, whiteSpace:"nowrap" }}>
      {children}
    </th>
  );
  const TD = ({ children, w, center }) => (
    <td style={{ padding:"0 14px", height:52, borderBottom:`1px solid ${theme.border}`, width:w, minWidth:w, textAlign:center?"center":"left", verticalAlign:"middle" }}>
      {children}
    </td>
  );

  const totalAtivos   = projetos.filter(p=>GRUPOS[0].stages.includes(p.stageProjeto||"planejamento")).length;
  const totalValor    = projetos.reduce((a,p)=>a+Number(p.valor||0),0);
  const totalConclui  = projetos.filter(p=>p.stageProjeto==="concluido").length;

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif", background:theme.bg, minHeight:"100vh" }}>
      {/* Header */}
      <div style={{ padding:"20px 28px 0", background:theme.bgSecond, borderBottom:`1px solid ${theme.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:20, marginBottom:16, flexWrap:"wrap" }}>
          <div style={{ display:"flex", gap:24 }}>
            {[["Ativos",totalAtivos,theme.text],["Valor total",fmt(totalValor),ORANGE],["Concluídos",totalConclui,"#10b981"]].map(([label,val,color])=>(
              <div key={label}>
                <div style={{ fontSize:11, color:theme.textMuted, fontWeight:500 }}>{label}</div>
                <div style={{ fontSize:20, fontWeight:800, color }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:10, alignItems:"center" }}>
            <input placeholder="🔍 Pesquisar projeto..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{ background:theme.bgCard, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"8px 14px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, width:220, fontFamily:"'Inter',sans-serif" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            <button onClick={()=>setModal({})} style={{ background:`linear-gradient(135deg,${ORANGE},#FF9A5C)`, border:"none", borderRadius:10, padding:"9px 20px", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif", boxShadow:`0 4px 12px ${ORANGE}44` }}>
              + Criar projeto
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ overflowX:"auto", padding:"16px 0 32px" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", tableLayout:"fixed", minWidth:820 }}>
          <thead>
            <tr>
              <TH w={"4%"}><input type="checkbox" onChange={e=>setSelected(e.target.checked?projetos.map(p=>p._id):[])}/></TH>
              <TH w={"15%"}>Cliente</TH>
              <TH w={"16%"}>Projeto</TH>
              <TH w={"18%"}>Progresso</TH>
              <TH w={"12%"} center>Etapa</TH>
              <TH w={"14%"}>Responsável</TH>
              <TH w={"17%"}>Datas e prazo</TH>
              <TH w={"4%"} center></TH>
            </tr>
          </thead>

          {loading ? (
            <tbody><tr><td colSpan={8} style={{ padding:60, textAlign:"center", color:theme.textMuted }}>Carregando projetos...</td></tr></tbody>
          ) : (
            GRUPOS.map(grupo => {
              const grupoLeads = getGrupo(grupo).slice().sort((a,b)=>((a.ordemProjeto??999999)-(b.ordemProjeto??999999)));
              const totalV     = grupoLeads.reduce((a,p)=>a+Number(p.valor||0),0);
              const colapsado  = colapsados[grupo.id];
              return (
                <tbody key={grupo.id}>
                  {/* Header grupo */}
                  <tr>
                    <td colSpan={8} style={{ padding:0, background:theme.bg }}>
                      <div onClick={()=>setColapsados(c=>({...c,[grupo.id]:!c[grupo.id]}))} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", cursor:"pointer", borderLeft:`3px solid ${grupo.color}`, borderBottom:`1px solid ${theme.border}`, background:grupo.color+"06", userSelect:"none" }}>
                        <span style={{ color:grupo.color, fontSize:14, display:"inline-block", transform:colapsado?"rotate(-90deg)":"rotate(0deg)", transition:"transform .2s" }}>▾</span>
                        <span style={{ fontWeight:700, color:grupo.color, fontSize:14 }}>{grupo.label}</span>
                        <span style={{ background:grupo.color+"25", color:grupo.color, borderRadius:99, fontSize:11, fontWeight:700, padding:"2px 10px" }}>{grupoLeads.length}</span>
                        <span style={{ marginLeft:"auto", fontSize:13, color:theme.textMuted, fontWeight:600 }}>{fmt(totalV)}</span>
                      </div>
                    </td>
                  </tr>

                  {!colapsado && grupoLeads.map(proj => {
                    const resp = proj.responsavelNome || proj.nomeResponsavel;
                    const isSel = selected.includes(proj._id);
                    const stageProjeto = proj.stageProjeto || "planejamento";
                    return (
                      <tr key={proj._id} draggable onDragStart={()=>setDragProj(proj._id)} onClick={()=>setCentral(proj)} onDragOver={e=>e.preventDefault()} onDrop={()=>{ if(dragProj && dragProj!==proj._id){ reordenarProjetos(grupoLeads, dragProj, proj._id); } setDragProj(null); }} style={{ background:(dragProj===proj._id?theme.bgHover:(isSel?ORANGE+"08":theme.bgCard)), transition:"background .1s", cursor:"pointer" }}
                        onMouseEnter={e=>{ if(!isSel)e.currentTarget.style.background=theme.bgHover; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background=isSel?ORANGE+"08":theme.bgCard; }}>
                        <TD w={36} center>
                          <input type="checkbox" checked={isSel} onClick={e=>e.stopPropagation()} onChange={()=>setSelected(prev=>prev.includes(proj._id)?prev.filter(x=>x!==proj._id):[...prev,proj._id])}/>
                        </TD>
                        <TD>
                          {proj.empresa ? <span style={{ display:"inline-flex", alignItems:"center", gap:7, fontSize:13, fontWeight:500, color:theme.text, maxWidth:"100%" }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color:theme.textMuted, flexShrink:0 }}><path d="M3 21h18"/><path d="M5 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16"/><path d="M19 21V11l-3-2"/><path d="M9 7h2M9 11h2M9 15h2"/></svg><span style={{ whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{proj.empresa}</span></span> : <span style={{ color:theme.textMuted, fontSize:11 }}>—</span>}
                        </TD>
                        <TD>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span draggable onClick={e=>e.stopPropagation()} onDragStart={e=>{ e.stopPropagation(); setDragProj(proj._id); }} onDragEnd={()=>setDragProj(null)} title="Arrastar para reordenar" style={{ cursor:"grab", color:theme.textMuted, fontSize:13, userSelect:"none", flexShrink:0 }}>⣿</span>
                            <div style={{ width:3, height:32, borderRadius:99, background:ETAPAS_PROJETO.find(e=>e.id===stageProjeto)?.color||theme.border, flexShrink:0 }}/>
                            <div style={{ flex:1, overflow:"hidden" }}>
                              <div style={{ fontWeight:600, color:theme.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{proj.nome||"—"}</div>

                            </div>
                          </div>
                        </TD>
                        <TD w={160}>
                          <div style={{ padding:"0 4px" }}>
                            {Array.isArray(proj.fasesProjeto)&&proj.fasesProjeto.length>0 ? (()=>{ const fs=proj.fasesProjeto; const pr=Math.round(fs.reduce((a,f)=>a+(f.status==="concluido"?100:f.status==="a_iniciar"?0:(f.checklist&&f.checklist.length?Math.round(f.checklist.filter(c=>c.feito).length/f.checklist.length*100):(f.inicio&&f.fim&&new Date(f.fim)>new Date(f.inicio)?Math.max(0,Math.min(100,Math.round((Date.now()-new Date(f.inicio))/(new Date(f.fim)-new Date(f.inicio))*100))):Number(f.percent||0)))),0)/fs.length); const cc=fs.filter(f=>f.status==="concluido").length; return (<div><div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:theme.textMuted, marginBottom:2 }}><span>{cc}/{fs.length} fases</span><span style={{ fontWeight:700, color:pr===100?"#1D9E75":ORANGE }}>{pr}%</span></div><div style={{ height:6, borderRadius:99, background:theme.bgInput, overflow:"hidden" }}><div style={{ height:"100%", width:pr+"%", background:pr===100?"#1D9E75":ORANGE }}/></div></div>); })() : <CronogramaBar inicio={proj.dataInicio} fim={proj.dataFechamento} concluido={proj.stageProjeto==="concluido"}/>}
                          </div>
                        </TD>
                        <TD w={160} center>
                          <EtapaBadge stage={stageProjeto} onChange={v=>atualizarStageProjeto(proj._id,v)}/>
                        </TD>
                        <TD w={180}>
                          {resp ? <div style={{ display:"flex", alignItems:"center", gap:8 }}><Avatar nome={resp} size={26}/><span style={{ fontSize:12.5, color:theme.text, fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{resp}</span></div> : <span style={{ color:theme.textMuted, fontSize:11, cursor:"pointer" }} onClick={()=>setModal(proj)}>+ Atribuir</span>}
                        </TD>


                        <TD>
                          {(()=>{ const dI = proj.dataInicio?fmtData(proj.dataInicio):"—"; const dF = proj.dataFechamento?fmtData(proj.dataFechamento):"—"; let pill; if(proj.stageProjeto==="concluido"){ pill=<span style={{ fontSize:11, fontWeight:600, color:"#0F6E56", background:"#1D9E7522", borderRadius:99, padding:"3px 9px", whiteSpace:"nowrap" }}>✓ Concluído</span>; } else if(!proj.dataFechamento){ pill=<span style={{ color:theme.textMuted, fontSize:11, cursor:"pointer" }} onClick={(e)=>{e.stopPropagation();setModal(proj);}}>+ prazo</span>; } else { const dias=Math.ceil((new Date(proj.dataFechamento)-new Date())/86400000); const atr=dias<0; const soon=!atr&&dias<=7; const cor=atr?"#E24B4A":soon?"#B4780F":"#5F5E5A"; const bg=atr?"#E24B4A1A":soon?"#EBA13A22":theme.bgInput; pill=<span style={{ fontSize:11, fontWeight:600, color:cor, background:bg, borderRadius:99, padding:"3px 9px", whiteSpace:"nowrap" }}>{atr?("atrasado "+Math.abs(dias)+"d"):("faltam "+dias+"d")}</span>; } return (<div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:10, alignItems:"center" }}><div><div style={{ fontSize:9, color:theme.textMuted, textTransform:"uppercase", letterSpacing:".04em" }}>Início</div><div style={{ fontSize:12, color:theme.text }}>{dI}</div></div><div><div style={{ fontSize:9, color:theme.textMuted, textTransform:"uppercase", letterSpacing:".04em" }}>Fim</div><div style={{ fontSize:12, color:theme.text }}>{dF}</div></div><div style={{ textAlign:"right" }}>{pill}</div></div>); })()}
                        </TD>

                        <TD w={80} center>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <button onClick={(e)=>{e.stopPropagation();setModal(proj);}} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:20, borderRadius:6, padding:"2px 8px", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center" }}
                              onMouseEnter={e=>{ e.currentTarget.style.background=theme.bgInput; e.currentTarget.style.color=theme.text; }}
                              onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>⋮</button>
                          </div>
                        </TD>
                      </tr>
                    );
                  })}

                  {!colapsado && novaLinhaGrupo===grupo.id && (
                    <NovaLinhaRow stageDefault={grupo.stages[0]} onAdd={adicionarProjeto} onCancel={()=>setNovaLinhaGrupo(null)}/>
                  )}

                  {!colapsado && (
                    <tr>
                      <td colSpan={8} style={{ padding:"6px 14px", borderBottom:`1px solid ${theme.border}`, background:theme.bg }}>
                        <button onClick={()=>setNovaLinhaGrupo(grupo.id)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:6, padding:"5px 8px", borderRadius:8, fontFamily:"'Inter',sans-serif" }}
                          onMouseEnter={e=>{ e.currentTarget.style.background=grupo.color+"10"; e.currentTarget.style.color=grupo.color; }}
                          onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.textMuted; }}>
                          + Adicionar projeto
                        </button>
                      </td>
                    </tr>
                  )}

                  {!colapsado && grupoLeads.length>0 && (
                    <tr style={{ background:grupo.color+"06" }}>
                      <td colSpan={8} style={{ padding:"8px 14px", fontSize:12, color:theme.textMuted, borderBottom:`1px solid ${theme.border}`, fontWeight:500 }}>
                        {grupoLeads.length} projeto{grupoLeads.length!==1?"s":""}
                      </td>
                    </tr>
                  )}
                  <tr><td colSpan={8} style={{ height:20, background:theme.bg }}/></tr>
                </tbody>
              );
            })
          )}
        </table>

        {!loading && projetos.length===0 && (
          <div style={{ textAlign:"center", padding:"60px 0", color:theme.textMuted }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🗂️</div>
            <div style={{ fontSize:16, fontWeight:600, color:theme.text, marginBottom:6 }}>Nenhum projeto ainda</div>
            <div style={{ fontSize:14 }}>Clique em "+ Criar projeto" para começar</div>
          </div>
        )}
      </div>


      {central && <ProjetoCentral projeto={central} usuarios={usuarios} onClose={()=>setCentral(null)} onUpdated={u=>{ setProjetos(prev=>prev.map(p=>p._id===u._id?u:p)); setCentral(u); }}/>}
            {modal!==null && (
        <ModalProjeto projeto={Object.keys(modal).length?modal:null} onClose={()=>setModal(null)} onSave={salvar} onDelete={excluir} usuarios={usuarios}/>
      )}
    </div>
  );
}
